import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { of, switchMap, map } from 'rxjs';
import { SiteSettingsService } from '../services/site-settings.service';
import { UserProfileService } from '../services/users/userDetails.service';

/**
 * Maintenance Guard
 * - When maintenance mode is active:
 *   - Admin routes (/admin/**, /auth/**) remain accessible.
 *   - If maintenance_allow_admin is active, authenticated administrators can access all user-facing routes.
 *   - /maintenance route is accessible so visitors see the maintenance page.
 * - When maintenance mode is inactive:
 *   - Normal navigation is allowed.
 *   - /maintenance redirects back to home / unless in preview mode (?preview=true).
 */
export const maintenanceGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const siteSettingsService = inject(SiteSettingsService);
  const profileService = inject(UserProfileService);

  const url = state.url || '';
  const cleanUrl = url.split('?')[0];
  const isMaintenancePage = cleanUrl === '/maintenance' || cleanUrl.startsWith('/maintenance/');
  const isAdminRoute = cleanUrl.startsWith('/admin') || cleanUrl.startsWith('/auth');

  // Admin routes and authentication are always accessible
  if (isAdminRoute) {
    return true;
  }

  return siteSettingsService.getMaintenanceStatus().pipe(
    switchMap((status) => {
      const isMaintenanceActive = status.maintenance_mode === true;
      const allowAdmin = status.maintenance_allow_admin !== false;

      if (!isMaintenanceActive) {
        if (isMaintenancePage && !route.queryParams['preview']) {
          return of(router.createUrlTree(['/']));
        }
        return of(true);
      }

      // When maintenance mode is active:
      // Check if logged-in admin can bypass maintenance
      if (allowAdmin && isPlatformBrowser(platformId)) {
        return profileService.isAdmin().pipe(
          map((isAdmin) => {
            if (isAdmin) {
              // Admin has full access to all user routes
              return true;
            }
            return true;
          })
        );
      }

      return of(true);
    })
  );
};
